import {
  NumberField,
  TextField,
  Labeled,
} from 'react-admin'
import {
  Grid, Typography,
} from '@mui/material'
import { Box } from '@mui/system'
import { useLabel, useSettings } from '../../hooks'
import ItemGrid from '../shared/dispatches/item-grid'
import LocationField from '../shared/dispatches/location-field'
import StackedDateTimeField from '../shared/dispatches/stacked-date-time-field'
import { getMaterialAndGrade, getTonnageRate } from '../shared/dispatches/util'
import AttachmentsList from '../shared/dispatches/attachments-list'

const brokerRateFields = [
  { source: 'broker_base_rate_in_dollars', flatSource: 'broker_is_flat_base_rate' },
  { source: 'broker_equipment_rate_in_dollars', flatSource: 'broker_is_flat_equipment_rate' },
  { source: 'broker_fuel_surcharge_in_dollars', flatSource: 'broker_is_flat_fuel_surcharge' },
  { source: 'broker_material_surcharge_in_dollars', flatSource: 'broker_is_flat_material_surcharge' },
]

const Content = ({ record, resource, sx, isPrintView = false }) => {
  const label = useLabel({ resource })
  const { isBrokerDriver } = useSettings()

  return (
    <Box sx={{
      ...sx,
      padding: isPrintView ? [4, 2] : 0,
    }}>
      <Grid
        container
        columnSpacing={2}
        rowSpacing={isPrintView ? 4 : 2.4}
      >
        <Grid item xs={12}>
          <Grid container columnSpacing={2} rowSpacing={isPrintView ? 4 : 2.4}>
            <Grid item xs={record.load_at ? 4 : 6}>
              <Labeled label={label('start_at')}>
                <StackedDateTimeField record={record} source='start_at' />
              </Labeled>
            </Grid>
            {
              record.load_at &&
              <Grid item xs={4}>
                <Labeled label={label('load_at')}>
                  <StackedDateTimeField record={record} source='load_at' />
                </Labeled>
              </Grid>
            }
            <Grid item xs={record.load_at ? 4 : 6}>
              <Labeled label={label('end_at')}>
                <StackedDateTimeField record={record} source='end_at' />
              </Labeled>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={6}>
          <LocationField
            label={label('pickup_site.name')}
            name={record.pickup_site.name}
            href={record.pickup_site.map_pin_link}
            address={record.pickup_site}
            isPrintView={isPrintView}
          />
        </Grid>
        <Grid item xs={6}>
          <LocationField
            label={label('dropoff_site.name')}
            name={record.dropoff_site.name}
            href={record.dropoff_site.map_pin_link}
            address={record.dropoff_site}
            isPrintView={isPrintView}
          />
        </Grid>
        <Grid item xs={6}>
          <Labeled label={label('customer.name')}>
            <TextField record={record} source='customer.name'/>
          </Labeled>
        </Grid>
        <Grid item xs={6}>
          <Labeled label={label('recipient.name')}>
            <TextField record={record} source='recipient.name'/>
          </Labeled>
        </Grid>
        <Grid item xs={6}>
          <Labeled label={label('material.name')}>
            <Typography variant='body2'>
              {getMaterialAndGrade(record.material.name, record.order.material_grade)}
            </Typography>
          </Labeled>
        </Grid>
        <Grid item xs={6} >
          <Labeled label={label('order.order_number')}>
            <TextField record={record} source='order.order_number'/>
          </Labeled>
        </Grid>
        {
          isBrokerDriver &&
          <Grid item xs={6}>
            <Labeled label={label('num_trucks')}>
              <NumberField record={record} source='num_trucks' />
            </Labeled>
          </Grid>
        }
        {
          isBrokerDriver && brokerRateFields.some(({ source }) => record[source] != null && record[source] !== 0) &&
          <Grid item xs={6}>
            <Labeled label={label('broker_rates')}>
              <Box>
                {brokerRateFields.map(({ source, flatSource }) => (
                  record[source] != null && record[source] !== 0 &&
                  <Typography variant='body2' fontSize='0.8em' key={source}>
                    {[label(source), getTonnageRate(record[source], record[flatSource])].join(': ')}
                  </Typography>
                ))}
              </Box>
            </Labeled>
          </Grid>
        }
        {
          record.terminal &&
          <Grid item xs={6} >
            <Labeled label={label('terminal.name')}>
              <TextField record={record} source='terminal.name'/>
            </Labeled>
          </Grid>
        }
        {
          record.notes &&
          <Grid item xs={12}>
            <Labeled label={label('notes')}>
              <TextField component='pre' record={record} source='notes' sx={{ whiteSpace: 'pre-line' }} />
            </Labeled>
          </Grid>
        }
        {
          record.attachments?.length > 0 &&
          <Grid item xs={12}>
            <AttachmentsList attachments={record.attachments} />
          </Grid>
        }
      </Grid>
    </Box>
  )
}

const TonnageDispatches = () => <ItemGrid ContentComponent={Content} />

export default TonnageDispatches
